import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom'; // Removed useHistory
import {
    Container,
    Paper,
    Typography,
    Box,
    Tabs,
    Tab,
    CircularProgress,
    Button,
    Card,
    CardContent,
    Grid,
    Divider,
    List,
    ListItem,
    ListItemText,
    Chip,
    IconButton,
    Collapse
} from '@mui/material';
import {
    ArrowLeft,
    FileText,
    CheckCircle,
    AlertTriangle,
    Download,
    Eye,
    Maximize2,
    Hand,
    X,
    GitCompareArrows,
    ChevronDown
} from 'lucide-react';
import { getData } from '../api/axiosConfig';
import { BASE_URL } from '../constants';
import { useAuth } from '../context/AuthContext';
import moment from 'moment';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const TabPanel = (props) => {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
            style={{ height: '100%', display: value === index ? 'flex' : 'none', flexDirection: 'column', overflowY: 'auto', flex: 1 }}
        >
            {value === index && (
                <Box sx={{ p: 3, height: '100%' }}>
                    {children}
                </Box>
            )}
        </div>
    );
};

export const FileDetails = () => {
    const { taskId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const userEmail = user?.email || 'anonymous@user.local';
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [tabValue, setTabValue] = useState(0);
    const [filterStatus, setFilterStatus] = useState('all');
    const [showCompare, setShowCompare] = useState(false);
    const [selectedChangeIdx, setSelectedChangeIdx] = useState(null);
    const [numPagesOriginal, setNumPagesOriginal] = useState(null);
    const [numPagesRemediated, setNumPagesRemediated] = useState(null);
    const [pdfContainerWidth, setPdfContainerWidth] = useState(0);
    const pdfScrollRef = useRef(null);

    // Measure the PDF scroll container width for dynamic page sizing
    useEffect(() => {
        if (!showCompare || !pdfScrollRef.current) return;
        const measure = () => {
            if (pdfScrollRef.current) {
                setPdfContainerWidth(pdfScrollRef.current.clientWidth);
            }
        };
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(pdfScrollRef.current);
        return () => observer.disconnect();
    }, [showCompare]);

    useEffect(() => {
        const fetchFileDetails = async () => {
            try {
                setLoading(true);
                const response = await getData(`/api/documents/${taskId}?user_id=${encodeURIComponent(userEmail)}`);
                setFile(response.document);
            } catch (err) {
                console.error("Error fetching file details:", err);
                setError("Failed to load file details.");
            } finally {
                setLoading(false);
            }
        };

        if (taskId) {
            fetchFileDetails();
        }
    }, [taskId]);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleBack = () => {
        navigate('/files');
    };

    const getStreamUrl = (type) => {
        if (!file?.task_id) return '';
        switch (type) {
            case 'original':
                return `${BASE_URL}/api/documents/stream-input-document?task_id=${file.task_id}&user_id=${encodeURIComponent(userEmail)}`;
            case 'remediated':
                return `${BASE_URL}/api/documents/stream-remediated?task_id=${file.task_id}&user_id=${encodeURIComponent(userEmail)}`;
            case 'report':
                return `${BASE_URL}/api/documents/stream-report?task_id=${file.task_id}&user_id=${encodeURIComponent(userEmail)}`;
            default:
                return '';
        }
    };

    // State for expanding grouped issues
    const [expandedGroups, setExpandedGroups] = useState({});
    const toggleGroup = (title) => {
        setExpandedGroups((prev) => ({ ...prev, [title]: !prev[title] }));
    };

    // Group issues by title for outstanding/manual views
    const groupIssuesByTitle = useCallback((issues) => {
        const groups = {};
        issues.forEach((issue) => {
            const key = issue.title || 'Unknown Issue';
            if (!groups[key]) {
                groups[key] = {
                    title: key,
                    severity: issue.severity,
                    wcag_criterion: issue.wcag_criterion,
                    fix_type: issue.fix_type,
                    was_fixed: issue.was_fixed,
                    was_assessed: issue.was_assessed,
                    instances: [],
                };
            }
            groups[key].instances.push(issue);
        });
        return Object.values(groups);
    }, []);

    // Compute grouped counts for outstanding and manual issues
    const outstandingIssues = useMemo(() =>
        (file?.issues_details || []).filter(i => !i.was_fixed && !i.was_assessed),
        [file?.issues_details]
    );
    const manualIssues = useMemo(() =>
        (file?.issues_details || []).filter(i => i.fix_type === 'manual' && !i.was_fixed),
        [file?.issues_details]
    );
    const groupedOutstandingCount = useMemo(() =>
        groupIssuesByTitle(outstandingIssues).length,
        [outstandingIssues, groupIssuesByTitle]
    );
    const groupedManualCount = useMemo(() =>
        groupIssuesByTitle(manualIssues).length,
        [manualIssues, groupIssuesByTitle]
    );

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4 }}>
                <Button startIcon={<ArrowLeft />} onClick={handleBack} sx={{ mb: 2 }}>
                    Back to Files
                </Button>
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="h5" color="error" gutterBottom>
                        Error
                    </Typography>
                    <Typography>{error}</Typography>
                </Paper>
            </Container>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" style={{ paddingBottom: '40px' }}>
            {/* Header Section */}
            <div className="mb-6">
                <Button
                    startIcon={<ArrowLeft size={18} />}
                    onClick={handleBack}
                    sx={{
                        mb: 2,
                        color: 'hsl(var(--muted-foreground))',
                        textTransform: 'none',
                        fontSize: '0.875rem',
                        '&:hover': { backgroundColor: 'hsl(var(--accent))', color: 'hsl(var(--foreground))' }
                    }}
                >
                    Back to Files
                </Button>

                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="service-icon" style={{ width: '48px', height: '48px' }}>
                            <FileText size={24} />
                        </div>
                        <div>
                            <div className="flex items-center gap-3">
                                <h2 className="text-2xl font-bold tracking-tight text-foreground">
                                    {file?.input_file_name || 'Document Details'}
                                </h2>
                                {(file?.status === 'completed' || file?.status === 'remediated') && (
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<GitCompareArrows size={16} />}
                                        onClick={() => setShowCompare(true)}
                                        sx={{
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            borderColor: 'hsl(var(--primary))',
                                            color: 'hsl(var(--primary))',
                                            borderRadius: '8px',
                                            height: '28px',
                                            fontSize: '0.75rem',
                                            '&:hover': {
                                                backgroundColor: 'hsl(var(--primary) / 0.05)',
                                                borderColor: 'hsl(var(--primary))',
                                            }
                                        }}
                                    >
                                        Compare
                                    </Button>
                                )}
                            </div>
                            <div className="flex items-center gap-4 mt-1">
                                <Chip
                                    label={file?.status?.toUpperCase()}
                                    color={(file?.status === 'completed' || file?.status === 'remediated') ? 'success' : 'default'}
                                    size="small"
                                    sx={{ fontWeight: 600, borderRadius: '6px', height: '20px', fontSize: '0.65rem' }}
                                />
                                <p className="text-sm text-muted-foreground flex items-center gap-1">
                                    Uploaded: {moment(file?.created_at).format('MMM D, YYYY h:mm A')}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <Paper
                elevation={0}
                className="ai-card animate-slide-up"
                sx={{
                    animationDelay: '0.1s',
                    p: 0,
                    overflow: 'hidden',
                    hoverScale: 'none',
                    backgroundColor: 'hsl(var(--card))',
                    height: 'calc(100vh - 220px)',
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
                    <Tabs value={tabValue} onChange={handleTabChange} aria-label="file details tabs">
                        <Tab label="Overview" sx={{ textTransform: 'none', fontWeight: 600 }} />
                        <Tab label="Original PDF" sx={{ textTransform: 'none', fontWeight: 600 }} />
                        <Tab label="Remediated PDF" sx={{ textTransform: 'none', fontWeight: 600 }} />
                        <Tab label="Compliance Report PDF" sx={{ textTransform: 'none', fontWeight: 600 }} />
                    </Tabs>
                </Box>

                {/* Tab 0: Overview */}
                <TabPanel value={tabValue} index={0} style={{ overflowY: 'auto' }}>
                    <Grid container spacing={3}>
                        {/* Highlights */}
                        <Grid item xs={12}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={4}>
                                    <Card variant="outlined" className="ai-card" sx={{ height: '100%', hoverScale: 'none' }}>
                                        <CardContent>
                                            <Typography color="text.secondary" gutterBottom>Compliance Score Improvement</Typography>
                                            <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px' }}>
                                                <div style={{ textAlign: 'center' }}>
                                                    <Typography variant="h4" sx={{ fontWeight: 600, color: '#9ca3af' }}>
                                                        {file?.initial_compliance_score || 0}%
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">Before</Typography>
                                                </div>
                                                <div style={{ paddingBottom: '10px' }}>
                                                    <ArrowLeft size={20} style={{ transform: 'rotate(180deg)', color: '#10b981' }} />
                                                </div>
                                                <div style={{ textAlign: 'center' }}>
                                                    <Typography variant="h3" sx={{ fontWeight: 700, color: 'hsl(var(--success))' }}>
                                                        {file?.compliance_score || 0}%
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">After</Typography>
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Card variant="outlined" className="ai-card" sx={{ height: '100%', hoverScale: 'none' }}>
                                        <CardContent>
                                            <Typography color="text.secondary" gutterBottom>Issues Fixed (AI)</Typography>
                                            <Typography variant="h3" sx={{ fontWeight: 700, color: 'hsl(var(--primary))' }}>
                                                {file?.issues_fixed || 0}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Card variant="outlined" className="ai-card" sx={{ height: '100%', hoverScale: 'none' }}>
                                        <CardContent>
                                            <Typography color="text.secondary" gutterBottom>Manual Attention Needed</Typography>
                                            <Typography variant="h3" sx={{ fontWeight: 700, color: 'hsl(var(--warning))' }}>
                                                {groupedOutstandingCount}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                        </Grid>

                        {/* Detailed Issues List */}
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    Detailed Findings ({file?.issues_details?.length || 0})
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Chip
                                        label="All"
                                        onClick={() => setFilterStatus('all')}
                                        color={filterStatus === 'all' ? 'primary' : 'default'}
                                        variant={filterStatus === 'all' ? 'filled' : 'outlined'}
                                    />
                                    <Chip
                                        label={`Resolved (${file?.issues_details?.filter(i => i.was_fixed).length || 0})`}
                                        onClick={() => setFilterStatus('resolved')}
                                        color={filterStatus === 'resolved' ? 'success' : 'default'}
                                        variant={filterStatus === 'resolved' ? 'filled' : 'outlined'}
                                        icon={<CheckCircle size={14} />}
                                    />


                                    <Chip
                                        label={`Outstanding (${groupedOutstandingCount})`}
                                        onClick={() => setFilterStatus('outstanding')}
                                        color={filterStatus === 'outstanding' ? 'warning' : 'default'}
                                        variant={filterStatus === 'outstanding' ? 'filled' : 'outlined'}
                                        icon={<AlertTriangle size={14} />}
                                    />
                                    <Chip
                                        label={`Manual (${groupedManualCount})`}
                                        onClick={() => setFilterStatus('manual')}
                                        color={filterStatus === 'manual' ? 'info' : 'default'}
                                        variant={filterStatus === 'manual' ? 'filled' : 'outlined'}
                                        icon={<Hand size={14} />}
                                    />
                                </Box>
                            </Box>

                            {file?.issues_details && file.issues_details.length > 0 ? (
                                <Box sx={{ border: '1px solid #e5e7eb', borderRadius: '12px' }}>
                                    <List disablePadding sx={{ pb: 10 }}>
                                        {(() => {
                                            const filteredIssues = file.issues_details.filter(issue => {
                                                if (filterStatus === 'all') return true;
                                                if (filterStatus === 'resolved') return issue.was_fixed;
                                                if (filterStatus === 'assessed') return issue.was_assessed && !issue.was_fixed;
                                                if (filterStatus === 'outstanding') return !issue.was_fixed && !issue.was_assessed;
                                                if (filterStatus === 'manual') return issue.fix_type === 'manual' && !issue.was_fixed;
                                                return true;
                                            });

                                            const shouldGroup = filterStatus === 'outstanding' || filterStatus === 'manual';

                                            if (shouldGroup) {
                                                const grouped = groupIssuesByTitle(filteredIssues);
                                                return grouped.map((group, gIdx) => {
                                                    const isExpanded = expandedGroups[group.title] || false;
                                                    const count = group.instances.length;
                                                    return (
                                                        <div key={group.title || gIdx}>
                                                            <ListItem
                                                                alignItems="flex-start"
                                                                sx={{
                                                                    px: 3, py: 2,
                                                                    cursor: 'pointer',
                                                                    transition: 'background-color 0.15s',
                                                                    '&:hover': { backgroundColor: 'hsl(var(--warning) / 0.04)' },
                                                                }}
                                                                onClick={() => toggleGroup(group.title)}
                                                            >
                                                                <Box sx={{ width: '100%' }}>
                                                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                            <IconButton
                                                                                size="small"
                                                                                sx={{
                                                                                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                                                                    transition: 'transform 0.2s',
                                                                                    p: 0.3,
                                                                                }}
                                                                            >
                                                                                <ChevronDown size={16} />
                                                                            </IconButton>
                                                                            <AlertTriangle size={18} color="hsl(var(--warning))" />
                                                                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                                                {group.title}
                                                                            </Typography>
                                                                            <Chip
                                                                                label={group.severity}
                                                                                size="small"
                                                                                variant="outlined"
                                                                                color={group.severity === 'Critical' ? 'error' : 'warning'}
                                                                                sx={{ height: '20px', fontSize: '0.7rem' }}
                                                                            />
                                                                            {count > 1 && (
                                                                                <Chip
                                                                                    label={`×${count} instances`}
                                                                                    size="small"
                                                                                    sx={{
                                                                                        height: '22px',
                                                                                        fontSize: '0.72rem',
                                                                                        fontWeight: 700,
                                                                                        backgroundColor: 'hsl(var(--warning) / 0.12)',
                                                                                        color: 'hsl(var(--warning))',
                                                                                        border: '1px solid hsl(var(--warning) / 0.25)',
                                                                                    }}
                                                                                />
                                                                            )}
                                                                        </div>
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            {count === 1 ? `Page ${group.instances[0].page_num}` : `${count} pages`}
                                                                        </Typography>
                                                                    </div>
                                                                    {!isExpanded && (
                                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, pl: 4.5 }}>
                                                                            {group.instances[0].description}
                                                                        </Typography>
                                                                    )}
                                                                    {!isExpanded && (
                                                                        <Box sx={{ pl: 4.5 }}>
                                                                            <Typography variant="caption" sx={{ display: 'block', color: 'hsl(var(--destructive))', fontWeight: 500 }}>
                                                                                Action: {group.fix_type === 'manual' ? "Requires Manual Review" : "Pending AI/Code Update"}
                                                                            </Typography>
                                                                            {group.wcag_criterion && (
                                                                                <Chip
                                                                                    label={`WCAG ${group.wcag_criterion}`}
                                                                                    size="small"
                                                                                    sx={{ height: '24px', backgroundColor: '#f3f4f6', mt: 1 }}
                                                                                />
                                                                            )}
                                                                        </Box>
                                                                    )}
                                                                </Box>
                                                            </ListItem>
                                                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                                                <Box sx={{ backgroundColor: 'hsl(var(--muted) / 0.3)', borderTop: '1px solid hsl(var(--border) / 0.5)' }}>
                                                                    {group.instances.map((instance, idx) => (
                                                                        <Box
                                                                            key={instance.finding_id || idx}
                                                                            sx={{
                                                                                px: 3, pl: 7, py: 1.5,
                                                                                borderBottom: idx < count - 1 ? '1px solid hsl(var(--border) / 0.3)' : 'none',
                                                                            }}
                                                                        >
                                                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                                                                <Typography variant="caption" sx={{ fontWeight: 600, color: 'hsl(var(--foreground) / 0.7)' }}>
                                                                                    Instance {idx + 1}
                                                                                </Typography>
                                                                                <Chip
                                                                                    label={`Page ${instance.page_num}`}
                                                                                    size="small"
                                                                                    sx={{ height: '18px', fontSize: '0.65rem', backgroundColor: 'hsl(var(--accent))' }}
                                                                                />
                                                                            </div>
                                                                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                                                                                {instance.description}
                                                                            </Typography>
                                                                            {instance.element_text && (
                                                                                <Typography variant="caption" sx={{
                                                                                    display: 'block', mt: 0.5,
                                                                                    color: 'hsl(var(--muted-foreground))',
                                                                                    fontStyle: 'italic', fontSize: '0.75rem',
                                                                                }}>
                                                                                    "{instance.element_text}"
                                                                                </Typography>
                                                                            )}
                                                                            {instance.wcag_criterion && (
                                                                                <Chip
                                                                                    label={`WCAG ${instance.wcag_criterion}`}
                                                                                    size="small"
                                                                                    sx={{ height: '20px', backgroundColor: '#f3f4f6', mt: 0.5 }}
                                                                                />
                                                                            )}
                                                                        </Box>
                                                                    ))}
                                                                </Box>
                                                            </Collapse>
                                                            <Divider component="li" />
                                                        </div>
                                                    );
                                                });
                                            }

                                            // Flat list for resolved / assessed / all
                                            return filteredIssues.map((issue, index) => (
                                                <div key={index}>
                                                    <ListItem alignItems="flex-start" sx={{ px: 3, py: 2 }}>
                                                        <Box sx={{ width: '100%' }}>
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                    {issue.was_fixed ?
                                                                        <CheckCircle size={18} color="hsl(var(--success))" /> :
                                                                        issue.was_assessed ?
                                                                            <Eye size={18} color="hsl(var(--primary))" /> :
                                                                            <AlertTriangle size={18} color="hsl(var(--warning))" />
                                                                    }
                                                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                                        {issue.title}
                                                                    </Typography>
                                                                    <Chip
                                                                        label={issue.severity}
                                                                        size="small"
                                                                        variant="outlined"
                                                                        color={issue.severity === 'Critical' ? 'error' : 'warning'}
                                                                        sx={{ height: '20px', fontSize: '0.7rem' }}
                                                                    />
                                                                    {issue.was_fixed && (
                                                                        <Chip
                                                                            label={issue.fix_type === 'llm' ? "Fixed by AI" : "Fixed by Code"}
                                                                            size="small"
                                                                            sx={{ height: '20px', fontSize: '0.7rem', backgroundColor: 'hsl(var(--success) / 0.1)', color: 'hsl(var(--success))' }}
                                                                        />
                                                                    )}
                                                                    {issue.was_assessed && !issue.was_fixed && (
                                                                        <Chip
                                                                            label="AI Assessed"
                                                                            size="small"
                                                                            sx={{ height: '20px', fontSize: '0.7rem', backgroundColor: 'hsl(var(--primary) / 0.1)', color: 'hsl(var(--primary))' }}
                                                                        />
                                                                    )}
                                                                </div>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    Page {issue.page_num}
                                                                </Typography>
                                                            </div>
                                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                                {issue.description}
                                                            </Typography>
                                                            {issue.was_assessed && !issue.was_fixed && issue.assessment_result && (
                                                                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'hsl(var(--primary))', fontStyle: 'italic' }}>
                                                                    AI Assessment: {issue.assessment_result.substring(0, 150)}
                                                                </Typography>
                                                            )}
                                                            {(!issue.was_fixed && !issue.was_assessed) && (
                                                                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'hsl(var(--destructive))', fontWeight: 500 }}>
                                                                    Action: {issue.fix_type === 'manual' ? "Requires Manual Review" : "Pending AI/Code Update"}
                                                                </Typography>
                                                            )}
                                                            {issue.wcag_criterion && (
                                                                <Chip
                                                                    label={`WCAG ${issue.wcag_criterion}`}
                                                                    size="small"
                                                                    sx={{ height: '24px', backgroundColor: '#f3f4f6', mt: 1 }}
                                                                />
                                                            )}
                                                        </Box>
                                                    </ListItem>
                                                    <Divider component="li" />
                                                </div>
                                            ));
                                        })()}
                                    </List>
                                </Box>
                            ) : (
                                <Typography color="text.secondary">No detailed findings available.</Typography>
                            )}
                        </Grid>
                    </Grid>
                </TabPanel>

                {/* Tab 1: Original PDF */}
                <TabPanel value={tabValue} index={1} style={{ padding: 0, height: '100%' }}>
                    <iframe
                        src={getStreamUrl('original')}
                        style={{ width: '100%', height: '100%', border: 'none' }}
                        title="Original PDF"
                    />
                </TabPanel>

                {/* Tab 2: Remediated PDF */}
                <TabPanel value={tabValue} index={2} style={{ padding: 0, height: '100%' }}>
                    <iframe
                        src={getStreamUrl('remediated')}
                        style={{ width: '100%', height: '100%', border: 'none' }}
                        title="Remediated PDF"
                    />
                </TabPanel>

                {/* Tab 3: Report PDF */}
                <TabPanel value={tabValue} index={3} style={{ padding: 0, height: '100%' }}>
                    <iframe
                        src={getStreamUrl('report')}
                        style={{ width: '100%', height: '100%', border: 'none' }}
                        title="Compliance Report PDF"
                    />
                </TabPanel>
            </Paper>

            {/* ───── Compare Overlay (Portal to body) ───── */}
            {showCompare && createPortal(
                <div style={{
                    position: 'fixed',
                    inset: 0,
                    zIndex: 9999,
                    backgroundColor: '#f9fafb',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}>
                    {/* Compare Header */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '12px 24px',
                        backgroundColor: 'hsl(var(--card))',
                        borderBottom: '1px solid hsl(var(--border))',
                        boxShadow: 'var(--shadow-subtle)',
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <GitCompareArrows size={22} color="hsl(var(--primary))" />
                            <Typography variant="h6" sx={{ fontWeight: 700, color: 'hsl(var(--foreground))' }}>
                                Compare: {file?.input_file_name}
                            </Typography>
                        </div>
                        <IconButton
                            onClick={() => { setShowCompare(false); setSelectedChangeIdx(null); }}
                            sx={{
                                backgroundColor: 'hsl(var(--accent))',
                                '&:hover': { backgroundColor: 'hsl(var(--accent) / 0.8)' },
                            }}
                        >
                            <X size={20} />
                        </IconButton>
                    </div>

                    {/* Compare Body */}
                    <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                        {/* Left+Middle — Synchronized PDFs in single scroll container */}
                        <div style={{ flex: '0 0 80%', display: 'flex', flexDirection: 'column', borderRight: '1px solid hsl(var(--border))' }}>
                            {/* Column headers */}
                            <div style={{ display: 'flex', borderBottom: '1px solid hsl(var(--border))', backgroundColor: 'hsl(var(--card))' }}>
                                <div style={{
                                    flex: 1,
                                    padding: '10px 16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    borderRight: '1px solid hsl(var(--border))',
                                }}>
                                    <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: 'hsl(var(--destructive))' }} />
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'hsl(var(--muted-foreground))' }}>Original PDF</Typography>
                                </div>
                                <div style={{
                                    flex: 1,
                                    padding: '10px 16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                }}>
                                    <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: 'hsl(var(--success))' }} />
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'hsl(var(--muted-foreground))' }}>Remediated PDF</Typography>
                                </div>
                            </div>
                            {/* Single scroll container with pages side by side */}
                            <div ref={pdfScrollRef} style={{ flex: 1, overflowY: 'auto', backgroundColor: 'hsl(var(--muted))', padding: '12px' }}>
                                {(() => {
                                    const maxPages = Math.max(numPagesOriginal || 0, numPagesRemediated || 0);
                                    const pageWidth = pdfContainerWidth ? (pdfContainerWidth - 36) / 2 : 400;
                                    return (
                                        <div style={{ position: 'relative' }}>
                                            {/* Hidden Document loaders to determine page count */}
                                            <Document
                                                file={getStreamUrl('original')}
                                                onLoadSuccess={({ numPages }) => setNumPagesOriginal(numPages)}
                                                loading=""
                                            >
                                                {null}
                                            </Document>
                                            <Document
                                                file={getStreamUrl('remediated')}
                                                onLoadSuccess={({ numPages }) => setNumPagesRemediated(numPages)}
                                                loading=""
                                            >
                                                {null}
                                            </Document>

                                            {maxPages > 0 ? (
                                                Array.from({ length: maxPages }, (_, i) => i + 1).map(pageNum => (
                                                    <div key={pageNum} style={{
                                                        display: 'flex',
                                                        gap: '12px',
                                                        marginBottom: '12px',
                                                        alignItems: 'flex-start',
                                                    }}>
                                                        {/* Original page */}
                                                        <div style={{
                                                            flex: 1,
                                                            backgroundColor: '#fff',
                                                            borderRadius: '4px',
                                                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                                            overflow: 'hidden',
                                                            position: 'relative',
                                                        }}>
                                                            {pageNum <= (numPagesOriginal || 0) ? (
                                                                <Document file={getStreamUrl('original')} loading="">
                                                                    <Page
                                                                        pageNumber={pageNum}
                                                                        width={pageWidth}
                                                                        renderTextLayer={false}
                                                                        renderAnnotationLayer={false}
                                                                    />
                                                                </Document>
                                                            ) : (
                                                                <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
                                                                    <Typography variant="caption">No page {pageNum}</Typography>
                                                                </div>
                                                            )}
                                                            <div style={{
                                                                position: 'absolute', bottom: 6, right: 8,
                                                                fontSize: '0.65rem', color: '#9ca3af',
                                                                backgroundColor: 'rgba(255,255,255,0.8)',
                                                                padding: '1px 6px', borderRadius: '4px',
                                                            }}>
                                                                Page {pageNum}
                                                            </div>
                                                        </div>
                                                        {/* Remediated page */}
                                                        <div style={{
                                                            flex: 1,
                                                            backgroundColor: '#fff',
                                                            borderRadius: '4px',
                                                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                                            overflow: 'hidden',
                                                            position: 'relative',
                                                        }}>
                                                            {pageNum <= (numPagesRemediated || 0) ? (
                                                                <Document file={getStreamUrl('remediated')} loading="">
                                                                    <Page
                                                                        pageNumber={pageNum}
                                                                        width={pageWidth}
                                                                        renderTextLayer={false}
                                                                        renderAnnotationLayer={false}
                                                                    />
                                                                </Document>
                                                            ) : (
                                                                <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
                                                                    <Typography variant="caption">No page {pageNum}</Typography>
                                                                </div>
                                                            )}
                                                            <div style={{
                                                                position: 'absolute', bottom: 6, right: 8,
                                                                fontSize: '0.65rem', color: '#9ca3af',
                                                                backgroundColor: 'rgba(255,255,255,0.8)',
                                                                padding: '1px 6px', borderRadius: '4px',
                                                            }}>
                                                                Page {pageNum}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))
                                            ) : (
                                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                                                    <CircularProgress />
                                                    <Typography sx={{ ml: 2, color: '#6b7280' }}>Loading PDFs...</Typography>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}
                            </div>
                        </div>

                        {/* Right — Changes List */}
                        <div style={{ flex: '0 0 20%', display: 'flex', flexDirection: 'column', backgroundColor: '#ffffff' }}>
                            <div style={{
                                padding: '10px 16px',
                                borderBottom: '1px solid #e5e7eb',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                            }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#6b7280' }}>
                                    Changes Made
                                </Typography>
                                <Chip
                                    label={file?.issues_details?.filter(i => i.was_fixed).length || 0}
                                    size="small"
                                    sx={{ backgroundColor: '#d1fae5', color: '#065f46', fontWeight: 700, height: 22 }}
                                />
                            </div>
                            <div style={{ flex: 1, overflowY: 'auto' }}>
                                {file?.issues_details?.filter(i => i.was_fixed).length > 0 ? (
                                    file.issues_details.filter(i => i.was_fixed).map((change, idx) => (
                                        <div
                                            key={idx}
                                            onClick={() => setSelectedChangeIdx(selectedChangeIdx === idx ? null : idx)}
                                            style={{
                                                padding: '12px 16px',
                                                cursor: 'pointer',
                                                borderBottom: '1px solid #f3f4f6',
                                                borderLeft: selectedChangeIdx === idx ? '3px solid hsl(var(--primary))' : '3px solid transparent',
                                                backgroundColor: selectedChangeIdx === idx ? '#eef2ff' : '#fff',
                                                transition: 'all 0.15s ease',
                                            }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                                                <CheckCircle size={14} color="hsl(var(--success))" />
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'hsl(var(--foreground))', fontSize: '0.8rem', lineHeight: 1.3 }}>
                                                    {change.title}
                                                </Typography>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                                <Chip
                                                    label={change.severity}
                                                    size="small"
                                                    variant="outlined"
                                                    color={change.severity === 'Critical' ? 'error' : 'warning'}
                                                    sx={{ height: 18, fontSize: '0.65rem' }}
                                                />
                                                <Chip
                                                    label={change.fix_type === 'llm' ? 'AI Fix' : 'Code Fix'}
                                                    size="small"
                                                    sx={{ height: 18, fontSize: '0.65rem', backgroundColor: change.fix_type === 'llm' ? '#e0e7ff' : '#d1fae5', color: change.fix_type === 'llm' ? '#3730a3' : '#065f46' }}
                                                />
                                                <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.65rem' }}>
                                                    Pg {change.page_num}
                                                </Typography>
                                            </div>

                                            {/* Expanded detail */}
                                            {selectedChangeIdx === idx && (
                                                <div style={{
                                                    marginTop: '10px',
                                                    padding: '10px',
                                                    backgroundColor: '#f9fafb',
                                                    borderRadius: '8px',
                                                    border: '1px solid #e5e7eb',
                                                }}>
                                                    <Typography variant="caption" sx={{ color: '#374151', lineHeight: 1.5, display: 'block' }}>
                                                        {change.description || 'No additional details available.'}
                                                    </Typography>
                                                    {(change.fix_type === 'code' && (!change.description || change.description.toLowerCase().includes('tag') || change.description.toLowerCase().includes('structure'))) && (
                                                        <Chip
                                                            label="Structural Change"
                                                            size="small"
                                                            sx={{ mt: 1, height: 20, fontSize: '0.65rem', backgroundColor: '#fef3c7', color: '#92400e' }}
                                                        />
                                                    )}
                                                    {change.wcag_criterion && (
                                                        <Chip
                                                            label={`WCAG ${change.wcag_criterion}`}
                                                            size="small"
                                                            sx={{ mt: 1, ml: 0.5, height: 20, fontSize: '0.65rem', backgroundColor: '#f3f4f6' }}
                                                        />
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ))
                                ) : (
                                    <div style={{ padding: '24px 16px', textAlign: 'center' }}>
                                        <Typography variant="body2" color="text.secondary">No changes detected.</Typography>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                , document.body)}
        </div>
    );
};
